import { Grid, Text, Paper, Stack, Image, Modal, Anchor } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { useRef, useState, useEffect } from 'react';

interface FeaturedNews {
    imagePath: string;
    title: string;
    topText: string;
    botText: string;
    questionList: QuestionList[];
}

interface QuestionList {
    mark: string;
    question: string;
    answer: string;
}

interface News {
    imagePath: string;
    title: string;
    date: string;
    text: string;
    link: string;
}

interface LineNews {
    date: string;
    title: string;
    text: string;
}

interface NewsData {
    featuredNews: FeaturedNews;
    news: News[];
    lineNews: LineNews[];
}

const NewsSection = () => {
    const autoplay = useRef(Autoplay({ delay: 5000 }));
    const [newsData, setNewsData] = useState<NewsData | null>(null);
    const [selectedNews, setSelectedNews] = useState<News | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch('home/news/news.json');
                const data: NewsData = await response.json();
                setNewsData(data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    if (!newsData) {
        return <div>Loading...</div>;
    }

    const lineNewsItems = newsData.lineNews.map((item, index) => (
        <Carousel.Slide
            key={index}
            style={{ display: 'flex', flexDirection: 'column', height: '100vh', margin: '5px 0' }}
        >
            <Grid className='line-news' justify="space-between" align="stretch" style={{ flex: 1 }}>
                <Grid.Col span={2} style={{ display: 'flex', flexDirection: 'column' }}>
                    <Paper className='line-news-dt' radius="lg" shadow='sm' style={{ flex: 1 }}>
                        <Text size="lg" fw={700} ta="center" c="white">
                            {item.date}
                        </Text>
                    </Paper>
                </Grid.Col>
                <Grid.Col span={10} style={{ display: 'flex', flexDirection: 'column' }}>
                    <Paper className='line-news-content' radius="lg" style={{ flex: 1 }}>
                        <Text size="lg" fw={500} pl="xs">
                            <b>{item.title}</b> {item.text}
                        </Text>
                    </Paper>
                </Grid.Col>
            </Grid>
        </Carousel.Slide>
    ));

    const rows = newsData.news.map((item, index) => (
        <tr
            key={index}
            className="news-board-row"
            onClick={() => setSelectedNews(item)}
            style={{ cursor: 'pointer' }}
        >
            <td className="news-board-index">{newsData.news.length - index}</td>
            <td className="news-board-title">{item.title}</td>
            <td className="news-board-date">{item.date}</td>
        </tr>
    ));

    return (
        <section id="news-section">
            <div className="news-container">
                <Text className='title' align="center">NEWS</Text>
                <Stack align='Stretch'>
                    <Carousel
                        plugins={[autoplay.current]}
                        align="start"
                        orientation="vertical"
                        height={160}
                        slideSize="20%"
                        loop
                        withControls={false}
                    >
                        {lineNewsItems}
                    </Carousel>

                    <div className="news-board">
                        <table className="news-board-table">
                            <thead>
                                <tr>
                                    <th className="news-board-index">No.</th>
                                    <th className="news-board-title">제목</th>
                                    <th className="news-board-date">날짜</th>
                                </tr>
                            </thead>
                            <tbody>{rows}</tbody>
                        </table>
                    </div>
                </Stack>
            </div>

            <Modal
                opened={selectedNews !== null}
                onClose={() => setSelectedNews(null)}
                title={selectedNews?.title}
                size="lg"
                styles={{
                    title: { fontWeight: 700, fontSize: '1.1rem' },
                }}
            >
                {selectedNews && (
                    <Stack>
                        {selectedNews.imagePath && (
                            <Image
                                src={selectedNews.imagePath}
                                radius="md"
                                alt={selectedNews.title}
                            />
                        )}
                        <Text size="sm" c="dimmed">{selectedNews.date}</Text>
                        <Text size="md" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                            {selectedNews.text}
                        </Text>
                        {selectedNews.link && (
                            <Anchor href={selectedNews.link} target="_blank" size="sm">
                                관련 링크 바로가기 →
                            </Anchor>
                        )}
                    </Stack>
                )}
            </Modal>
        </section>
    );
};

export default NewsSection;
